import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaCheck,
  FaClock,
  FaExclamationTriangle,
  FaRedo,
  FaSync,
} from 'react-icons/fa';
import FinanceService from '../services/FinanceService';
import OpenFinanceConnectButton from './OpenFinanceConnectButton';
import MercadoPagoConnectButton from './MercadoPagoConnectButton';

const DEFAULT_POLL_INTERVAL_MS = 30_000;
const ACTIVE_SYNC_POLL_INTERVAL_MS = 5_000;

const connectionCopy = (status) => {
  if (status?.requiresAction) {
    return {
      title: 'Reconexão necessária',
      detail: 'Conecte novamente para retomar a sincronização automática.',
      tone: 'border-amber-200 bg-amber-50 text-amber-900',
    };
  }
  if (
    status?.connectionStatus === 'ACTIVE' &&
    status?.consentStatus === 'AUTHORISED'
  ) {
    return {
      title: 'Banco conectado com segurança',
      detail: 'O extrato está protegido e será atualizado automaticamente.',
      tone: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    };
  }
  if (status?.connectionStatus === 'AWAITING_AUTH') {
    return {
      title: 'Conexão bancária em andamento',
      detail: 'Conclua as etapas do banco para iniciar a sincronização.',
      tone: 'border-sky-200 bg-sky-50 text-sky-900',
    };
  }
  return {
    title: 'Banco ainda não conectado',
    detail: 'Use Open Finance ou credenciais para sincronizar o extrato automaticamente.',
    tone: 'border-slate-200 bg-slate-50 text-slate-800',
  };
};

const syncCopy = (latestSync) => {
  if (!latestSync) {
    return 'Aguardando a primeira sincronização do extrato.';
  }
  if (latestSync.status === 'SUCCEEDED' || latestSync.status === 'PARTIAL') {
    const inserted = Number(latestSync.insertedCount || 0);
    const updated = Number(latestSync.updatedCount || 0);
    const skipped = Number(latestSync.skippedCount || 0);
    return `${inserted} ${inserted === 1 ? 'nova' : 'novas'}, ${updated} ${updated === 1 ? 'atualizada' : 'atualizadas'} e ${skipped} ${skipped === 1 ? 'já existente' : 'já existentes'}.`;
  }
  if (latestSync.status === 'PENDING') {
    return 'A sincronização está na fila e começará em instantes.';
  }
  if (latestSync.status === 'RUNNING') {
    return 'O extrato está sendo sincronizado. Esta tela atualiza sozinha.';
  }
  return 'A última sincronização não foi concluída. Tente novamente.';
};

const fallbackValidation = (status) => {
  const bankAuthorization =
    status?.connectionStatus === 'ACTIVE' &&
    status?.consentStatus === 'AUTHORISED'
      ? 'PASSED'
      : status?.connectionStatus === 'AWAITING_AUTH'
        ? 'IN_PROGRESS'
        : 'ACTION_REQUIRED';
  const latestSyncStatus = status?.latestSync?.status;
  const firstSynchronization =
    latestSyncStatus === 'SUCCEEDED' || latestSyncStatus === 'PARTIAL'
      ? 'PASSED'
      : latestSyncStatus === 'FAILED'
        ? 'ACTION_REQUIRED'
        : 'IN_PROGRESS';
  const dataIntegrity = 'IN_PROGRESS';
  const checks = {
    exclusiveSource: 'PASSED',
    bankAuthorization,
    firstSynchronization,
    dataIntegrity,
  };
  const completedSteps = Object.values(checks).filter(
    (value) => value === 'PASSED',
  ).length;
  return {
    status: Object.values(checks).includes('ACTION_REQUIRED')
      ? 'ACTION_REQUIRED'
      : 'IN_PROGRESS',
    completedSteps,
    totalSteps: 4,
    checks,
    transactions: {
      totalCount: 0,
      creditCount: 0,
      debitCount: 0,
      integrityIssueCount: 0,
      duplicateCount: 0,
    },
  };
};

const formatTimestamp = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const countLabel = (value, singular, plural) => {
  const count = Number(value || 0);
  return `${count} ${count === 1 ? singular : plural}`;
};

const stepIcon = (stepStatus) => {
  if (stepStatus === 'PASSED') {
    return <FaCheck aria-hidden="true" className="h-3 w-3" />;
  }
  if (stepStatus === 'ACTION_REQUIRED') {
    return <FaExclamationTriangle aria-hidden="true" className="h-3 w-3" />;
  }
  return <FaClock aria-hidden="true" className="h-3 w-3" />;
};

const statusClasses = (stepStatus) => {
  if (stepStatus === 'PASSED') return 'bg-emerald-600 text-white';
  if (stepStatus === 'ACTION_REQUIRED') return 'bg-amber-500 text-white';
  return 'bg-slate-200 text-slate-600';
};

export default function OpenFinanceConnectionStatus({
  account,
  refreshKey = 0,
  canManage = false,
  showAlert,
  onConnected,
  onTransactionsRefresh,
  autoStart = false,
  onAutoStart,
}) {
  const [status, setStatus] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [requestingSync, setRequestingSync] = useState(false);
  const accountId = Number(account?.id);
  const eligible =
    account?.integrationType === 'API' &&
    Number.isInteger(accountId) &&
    accountId > 0;
  const syncInProgress = ['PENDING', 'RUNNING'].includes(
    status?.latestSync?.status,
  );

  useEffect(() => {
    if (!eligible) {
      setStatus(null);
      setUnavailable(false);
      return undefined;
    }

    let active = true;
    const load = async () => {
      try {
        const { data } = await FinanceService.getOpenFinanceStatus(accountId);
        if (!active) return;
        setStatus(data);
        setUnavailable(false);
      } catch (_error) {
        if (!active) return;
        setStatus(null);
        setUnavailable(true);
      }
    };

    void load();
    const timer = setInterval(
      load,
      syncInProgress
        ? ACTIVE_SYNC_POLL_INTERVAL_MS
        : DEFAULT_POLL_INTERVAL_MS,
    );
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [accountId, eligible, refreshKey, reloadKey, syncInProgress]);

  const validation = useMemo(
    () => (status ? status.validation || fallbackValidation(status) : null),
    [status],
  );

  const handleConnected = useCallback(
    async (data) => {
      setReloadKey((value) => value + 1);
      await onConnected?.(data);
    },
    [onConnected],
  );

  const handleSync = useCallback(async () => {
    if (!canManage || !eligible || requestingSync || syncInProgress) return;
    setRequestingSync(true);
    try {
      await FinanceService.syncAccount(accountId);
      showAlert?.(
        'success',
        'Sincronização solicitada. A validação será atualizada automaticamente.',
      );
      setReloadKey((value) => value + 1);
      await onTransactionsRefresh?.();
    } catch (_error) {
      showAlert?.(
        'error',
        'Não foi possível sincronizar e validar agora. Tente novamente em instantes.',
      );
    } finally {
      setRequestingSync(false);
    }
  }, [
    accountId,
    canManage,
    eligible,
    onTransactionsRefresh,
    requestingSync,
    showAlert,
    syncInProgress,
  ]);

  if (!eligible) return null;

  if (unavailable) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
        <p role="status" className="font-semibold">Status temporariamente indisponível</p>
        <p className="mt-1 text-xs text-slate-500">Não foi possível atualizar a jornada agora. Seus dados bancários continuam protegidos.</p>
        <button type="button" onClick={() => setReloadKey((value) => value + 1)} className="mt-3 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100">
          <FaRedo /> Tentar novamente
        </button>
      </div>
    );
  }

  if (!status || !validation) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        <p role="status">Preparando a conexão bancária segura…</p>
        {autoStart && (
          <div className="mt-3">
            <OpenFinanceConnectButton
              account={account}
              canManage={canManage}
              showAlert={showAlert}
              onConnected={handleConnected}
              autoStart={autoStart}
              onAutoStart={onAutoStart}
              buttonLabel="Escolher banco e conectar"
            />
          </div>
        )}
      </div>
    );
  }

  const connection = connectionCopy(status);
  const transactions = validation.transactions || {};
  const lastSyncedAt = formatTimestamp(status.lastSyncedAt);
  const nextSyncAt = formatTimestamp(status.nextSyncAt);
  const isConnected =
    status.connectionStatus === 'ACTIVE' &&
    status.consentStatus === 'AUTHORISED' &&
    !status.requiresAction;
  const connectLabel = status.requiresAction
    ? 'Reconectar banco'
    : status.connectionStatus === 'AWAITING_AUTH'
      ? 'Continuar conexão'
      : 'Escolher banco e conectar';
  const steps = [
    {
      id: 'exclusiveSource',
      title: 'Origem protegida',
      detail: 'Somente a integração bancária automática pode criar lançamentos nesta conta.',
    },
    {
      id: 'bankAuthorization',
      title: 'Banco conectado',
      detail: connection.detail,
    },
    {
      id: 'firstSynchronization',
      title: 'Extrato sincronizado',
      detail: syncCopy(status.latestSync),
    },
    {
      id: 'dataIntegrity',
      title: 'Dados do extrato validados',
      detail:
        validation.checks.dataIntegrity === 'PASSED'
          ? `${Number(transactions.totalCount || 0)} lançamentos verificados: ${Number(transactions.creditCount || 0)} créditos e ${Number(transactions.debitCount || 0)} débitos, sem duplicidades.`
          : validation.checks.dataIntegrity === 'ACTION_REQUIRED'
            ? `Encontramos ${countLabel(transactions.integrityIssueCount, 'inconsistência', 'inconsistências')} de classificação e ${countLabel(transactions.duplicateCount, 'lançamento duplicado', 'lançamentos duplicados')}. Sincronize novamente para validar.`
            : 'A validação acontece automaticamente após a primeira sincronização.',
    },
  ];

  return (
    <div className={`rounded-2xl border p-4 text-sm ${connection.tone}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p role="status" aria-live="polite" className="font-semibold">{connection.title}</p>
          <p className="mt-1 text-xs opacity-75">
            {validation.completedSteps} de {validation.totalSteps} etapas concluídas
          </p>
        </div>
        <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-semibold">
          {validation.status === 'READY'
            ? 'Integração validada'
            : validation.status === 'ACTION_REQUIRED'
              ? 'Ação necessária'
              : 'Configuração em andamento'}
        </span>
      </div>

      <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/70" aria-hidden="true">
        <div className="h-full rounded-full bg-emerald-600 transition-all" style={{ width: `${(validation.completedSteps / validation.totalSteps) * 100}%` }} />
      </div>

      <ol className="mt-4 space-y-3">
        {steps.map((step) => {
          const currentStatus = validation.checks[step.id] || 'IN_PROGRESS';
          return (
            <li key={step.id} className="flex gap-3 rounded-xl bg-white/70 p-3 text-slate-700">
              <span className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${statusClasses(currentStatus)}`}>
                {stepIcon(currentStatus)}
              </span>
              <span>
                <span className="block text-xs font-semibold text-slate-800">{step.title}</span>
                <span className="mt-0.5 block text-xs text-slate-500">{step.detail}</span>
              </span>
            </li>
          );
        })}
      </ol>

      {(lastSyncedAt || nextSyncAt) && (
        <p className="mt-3 text-xs opacity-75">
          {lastSyncedAt ? `Última atualização: ${lastSyncedAt}.` : ''}
          {nextSyncAt ? ` Próxima automática: ${nextSyncAt}.` : ''}
        </p>
      )}

      {canManage && (
        <div className="mt-4 flex flex-wrap gap-2">
          {!isConnected && (
            <>
              <MercadoPagoConnectButton
                account={account}
                canManage={canManage}
                showAlert={showAlert}
                onConnected={handleConnected}
              />
              <OpenFinanceConnectButton
                account={account}
                canManage={canManage}
                showAlert={showAlert}
                onConnected={handleConnected}
                autoStart={autoStart}
                onAutoStart={onAutoStart}
                buttonLabel={connectLabel}
              />
            </>
          )}
          {isConnected && (
            <button
              type="button"
              onClick={handleSync}
              disabled={requestingSync || syncInProgress}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
            >
              <FaSync className={syncInProgress ? 'animate-spin' : ''} />
              {requestingSync
                ? 'Solicitando…'
                : syncInProgress
                  ? 'Sincronizando e validando…'
                  : 'Sincronizar e validar agora'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
